"use client";
import React, { useRef, useEffect, useState } from "react";
import { useCountUp } from "use-count-up";
import useFramerMotion, {
  LazyMotion,
  m,
  useMotionValue,
  useInView,
  useReducedMotion,
} from "@/hooks/useFramerMotion";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { cn } from "@/utils";
import { useTranslations } from "next-intl";

type Props = {
  className?: string;
  itemClassName?: string;
  float?: boolean;
};

type CounterProps = { item: Item; itemClassName?: string; float?: boolean };

const delay = 2000;
const years = new Date().getFullYear() - 2001;
const stats = [
  {
    num: years,
    label: "yearsOfExperience",
  },
  {
    num: 250,
    prefix: "+",
    label: "projectsCompleted",
  },
  {
    num: 12,
    label: "technologiesMastered",
  },
  {
    num: 150,
    prefix: "+",
    label: "happyClients",
  },
];

interface Item {
  num: number;
  prefix?: string;
  label: string;
}

const getRandomPos = (max: number = 20) => Math.random() * (max * 2) - max;
const getRandomDuration = () => Math.random() * 11 + 4;
const degToRad = (deg: number) => deg * (Math.PI / 180);

const Stats = ({ className, ...props }: Props) => {
  return (
    <div className={cn("grid grid-cols-2 ~gap-2/6 xl:grid-cols-4", className)}>
      {stats.map((item) => (
        <Counter
          key={item.label}
          item={item}
          {...props}
        />
      ))}
    </div>
  );
};

const Counter = ({ item, itemClassName, float = true }: CounterProps) => {
  const framerMotionFeatures = useFramerMotion();
  const { md } = useBreakpoint();
  const shouldReduceMotion = useReducedMotion();
  // Gate the decorative floating drift on prefers-reduced-motion.
  const shouldFloat = float && !shouldReduceMotion;

  const xBase = useRef(20);
  const yBase = useRef(20);

  const [duration, setDuration] = useState(10);
  const mX = useMotionValue(0);
  const mY = useMotionValue(0);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [shouldRunCounter, setShouldRunCounter] = useState(false);
  const t = useTranslations("stats");

  useEffect(() => {
    if (shouldFloat) {
      mX.set(getRandomPos(xBase.current));
      mY.set(getRandomPos(yBase.current));
    }
    if (isInView && !shouldRunCounter) {
      setTimeout(() => setShouldRunCounter(true), delay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView, shouldRunCounter]);

  useEffect(() => {
    if (float) {
      xBase.current = md ? 40 : 20;
      yBase.current = md ? 40 : 20;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [md]);

  const { value } = useCountUp({
    start: 0,
    isCounting: shouldRunCounter,
    end: item.num,
    duration: 5,
    easing: "easeOutCubic",
    formatter: (value) => value.toFixed(),
  });
  // Reduced motion: show the final number instead of counting up to it.
  const displayValue = shouldReduceMotion ? item.num : value;

  return (
    <LazyMotion features={framerMotionFeatures}>
      <m.div
        animate={{
          x: mX.get(),
          y: mY.get(),
        }}
        transition={{
          duration: duration,
          ease: "easeInOut",
          type: "tween",
        }}
        onAnimationComplete={() => {
          if (shouldFloat) {
            setDuration(getRandomDuration());
            mX.set(
              Math.cos(duration) / 20 +
                getRandomPos(xBase.current) +
                degToRad(12)
            );
            mY.set(
              Math.sin(duration) / 50 +
                getRandomPos(yBase.current) +
                degToRad(12)
            );
          }
        }}
        ref={ref}
        className={cn(
          "relative z-20 flex aspect-square min-w-32 flex-col items-center justify-center gap-1 rounded-full border-2 border-primary bg-dark/45 font-secondary md:min-w-52",
          itemClassName
        )}>
        <span className="relative font-extrabold text-primary ~text-4xl/6xl">
          {item.prefix && (
            <span className="absolute right-full top-1/2 -translate-y-1/2 ~text-xl/4xl">
              {item.prefix}
            </span>
          )}
          {displayValue}
        </span>
        <p
          className={`max-w-[75%] text-center leading-snug text-mint ~text-sm/base`}>
          {t(item.label)}
        </p>
      </m.div>
    </LazyMotion>
  );
};

export default Stats;
